import React, { useState, useEffect, useMemo } from 'react';
import {
  Tag,
  Plus,
  Printer,
  FileDown,
  FileSpreadsheet,
  Trash2,
  Edit3,
  Copy,
  Search,
  X,
  CheckCircle2,
  Package,
  Building2,
  MapPin,
  RefreshCw,
  Sparkles,
  RotateCcw,
  BookmarkCheck
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

// Official Universal Electricals and Electronics Unit Presets
const UNIT_PRESETS = {
  unit1: {
    fromName: 'UNIVERSAL ELECTRICALS AND ELECTRONICS',
    fromAddressLine1: '15 & 16, 1st Floor, Girija Arcade, Lakshmipura Main Road,',
    fromAddressLine2: 'Opp PMK, Abbigere, Bangalore,',
    fromStatePincode: 'Karnataka - 560090',
    fromContactName: 'UNIVERSAL ELECTRICALS & ELECTRONICS',
    fromContactNo: '+91 84949 00615',
    fromGstin: '29AHKPH7026A1ZK'
  },
  unit2: {
    fromName: 'UNIVERSAL ELECTRICALS & ELECTRONICS',
    fromAddressLine1: 'Balaji Enclave 11/1, Pipeline Road Kempapura,',
    fromAddressLine2: 'Chikkabanavara Kempapura, Bangalore,',
    fromStatePincode: 'Karnataka - 560090',
    fromContactName: 'UNIVERSAL ELECTRICALS & ELECTRONICS',
    fromContactNo: '+91 96401 27499',
    fromGstin: '29AHKPH7026A1ZK'
  }
};

const STORAGE_KEY = 'uee_dispatch_stickers_v1';
const CUSTOM_FROM_KEY = 'uee_dispatch_default_from_v1';

function getInitialDefaultFrom() {
  try {
    const savedCustom = localStorage.getItem(CUSTOM_FROM_KEY);
    if (savedCustom) {
      const parsed = JSON.parse(savedCustom);
      if (parsed && parsed.fromName) return parsed;
    }
  } catch (e) {}
  const activeUnit = (typeof window !== 'undefined' && localStorage.getItem('uee_unit')) || 'unit1';
  return UNIT_PRESETS[activeUnit] || UNIT_PRESETS.unit1;
}

// Escape special characters for raw PDF text strings
function pdfEscape(str) {
  return String(str || '')
    .replace(/[^\x20-\x7E]/g, ' ')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

// Approximate text width in mm for Helvetica-Bold at a given pt size
function estimateHelveticaBoldWidthMm(text, fontSizePt) {
  const s = String(text || '');
  let units = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if ('I1.,:;!| '.includes(ch)) units += 0.32;
    else if ('MW@#%&'.includes(ch)) units += 0.88;
    else if (ch >= 'A' && ch <= 'Z') units += 0.68;
    else units += 0.56;
  }
  return units * fontSizePt * 0.352778;
}

/**
 * Pure zero-dependency vector PDF generator (A4 Portrait, Half-A4 Sticker layout).
 * Produces a crisp vector PDF matching the official label design without requiring external libraries.
 */
function generateHalfA4StickerPdfBlob(item) {
  const mmToPt = (mm) => +(mm * 2.83464567).toFixed(2);
  const pageH = 297;
  const yPt = (mmFromTop) => mmToPt(pageH - mmFromTop);

  const startX = 15;
  const startY = 12;
  const boxW = 180;
  const boxH = 132;
  const centerX = startX + boxW / 2;

  const ops = [];

  // 1. Outer Thick Solid Border (1.4mm = ~4pt)
  ops.push('0 0 0 RG');
  ops.push('0 0 0 rg');
  ops.push(`${mmToPt(1.4)} w`);
  ops.push(
    `${mmToPt(startX)} ${mmToPt(pageH - (startY + boxH))} ${mmToPt(boxW)} ${mmToPt(boxH)} re S`
  );

  // Helper to draw left-aligned bold text
  const drawTextLeft = (text, xMm, yMm, sizePt) => {
    const clean = pdfEscape(text);
    if (!clean) return;
    ops.push('BT');
    ops.push(`/F1 ${sizePt} Tf`);
    ops.push(`${mmToPt(xMm)} ${yPt(yMm)} Td`);
    ops.push(`(${clean}) Tj`);
    ops.push('ET');
  };

  // Helper to draw center-aligned bold text
  const drawTextCenter = (text, cXMm, yMm, sizePt) => {
    const clean = String(text || '').trim();
    if (!clean) return;
    const wMm = estimateHelveticaBoldWidthMm(clean, sizePt);
    const xMm = cXMm - wMm / 2;
    drawTextLeft(clean, xMm, yMm, sizePt);
  };

  // ----------------- 1. TO (CONSIGNEE) SECTION -----------------
  drawTextLeft('TO', startX + 10, startY + 14, 22);
  drawTextLeft(item.toName || 'RECIPIENT NAME', startX + 10, startY + 23, 17.5);

  let currY = startY + 30;
  if (item.toAddressLine1) {
    drawTextLeft(item.toAddressLine1, startX + 10, currY, 13);
    currY += 5.8;
  }
  if (item.toAddressLine2) {
    drawTextLeft(item.toAddressLine2, startX + 10, currY, 13);
    currY += 5.8;
  }
  if (item.toPincode) {
    drawTextLeft(`PIN CODE : ${item.toPincode}`, startX + 10, currY, 13.5);
    currY += 6.5;
  }

  drawTextLeft(`Contact Name : ${item.toContactName || item.toName || ''}`, startX + 10, currY, 12.5);
  currY += 5.8;
  drawTextLeft(`Contact No : ${item.toContactNo || ''}`, startX + 10, currY, 12.5);
  if (item.toGst) {
    currY += 5.8;
    drawTextLeft(`GSTIN : ${item.toGst}`, startX + 10, currY, 12);
  }

  // Dashed divider between TO and FROM
  const divY = Math.max(currY + 5, startY + 66);
  ops.push('0.35 0.35 0.35 RG');
  ops.push(`${mmToPt(0.45)} w`);
  ops.push('[4 3] 0 d');
  ops.push(`${mmToPt(startX + 6)} ${yPt(divY)} m ${mmToPt(startX + boxW - 6)} ${yPt(divY)} l S`);
  ops.push('[] 0 d');
  ops.push('0 0 0 RG');

  // ----------------- 2. FROM (UNIVERSAL EE) SECTION -----------------
  let fromY = divY + 8;

  // "FROM," on Left
  drawTextLeft('FROM,', startX + 10, fromY, 19);
  fromY += 7.5;

  // Sender Company Name Centered
  drawTextCenter(
    item.fromName || 'UNIVERSAL ELECTRICALS AND ELECTRONICS',
    centerX,
    fromY,
    15.5
  );
  fromY += 6.8;

  if (item.fromAddressLine1) {
    drawTextCenter(item.fromAddressLine1, centerX, fromY, 12.5);
    fromY += 5.8;
  }
  if (item.fromAddressLine2) {
    drawTextCenter(item.fromAddressLine2, centerX, fromY, 12.5);
    fromY += 5.8;
  }
  if (item.fromStatePincode) {
    drawTextCenter(item.fromStatePincode, centerX, fromY, 13);
    fromY += 6.2;
  }

  if (item.fromContactName) {
    drawTextCenter(`Contact Name : ${item.fromContactName}`, centerX, fromY, 12);
    fromY += 5.6;
  }
  if (item.fromContactNo) {
    drawTextCenter(`Contact No : ${item.fromContactNo}`, centerX, fromY, 12);
    fromY += 5.6;
  }
  if (item.fromGstin) {
    drawTextCenter(`GSTIN : ${item.fromGstin}`, centerX, fromY, 12);
  }

  // ----------------- 3. HALF-A4 CUT LINE -----------------
  const cutLineY = startY + boxH + 6;
  ops.push('0.4 0.45 0.52 RG');
  ops.push(`${mmToPt(0.4)} w`);
  ops.push('[7 5] 0 d');
  ops.push(`${mmToPt(startX)} ${yPt(cutLineY)} m ${mmToPt(startX + boxW)} ${yPt(cutLineY)} l S`);
  ops.push('[] 0 d');

  ops.push('0.4 0.45 0.52 rg');
  const cutNote = 'Cut along the solid outer border and paste securely onto parcel / cargo box';
  const noteWMm = estimateHelveticaBoldWidthMm(cutNote, 8.5);
  ops.push('BT');
  ops.push('/F2 8.5 Tf');
  ops.push(`${mmToPt(centerX - noteWMm / 2)} ${yPt(cutLineY + 4.5)} Td`);
  ops.push(`(${pdfEscape(cutNote)}) Tj`);
  ops.push('ET');

  const streamContent = ops.join('\n');

  // Assemble valid PDF 1.4 document
  const objects = [
    '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj',
    '2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj',
    '3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595.28 841.89] /Resources << /Font << /F1 4 0 R /F2 5 0 R >> >> /Contents 6 0 R >>\nendobj',
    '4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>\nendobj',
    '5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Oblique /Encoding /WinAnsiEncoding >>\nendobj',
    `6 0 obj\n<< /Length ${streamContent.length} >>\nstream\n${streamContent}\nendstream\nendobj`
  ];

  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  for (const obj of objects) {
    offsets.push(pdf.length);
    pdf += obj + '\n';
  }
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += '0000000000 65535 f \n';
  for (let i = 1; i <= objects.length; i++) {
    pdf += String(offsets[i]).padStart(10, '0') + ' 00000 n \n';
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
}

export default function DispatchStickersAdmin({
  quotations: propQuotations = [],
  proformaInvoices: propProformas = [],
  purchaseOrders: propPurchaseOrders = []
}) {
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSticker, setEditingSticker] = useState(null);
  const [printStickerItem, setPrintStickerItem] = useState(null);

  // Client / Vendor records for Quick Autofill (Both TO and FROM)
  const [quotations, setQuotations] = useState(propQuotations);
  const [proformaInvoices, setProformaInvoices] = useState(propProformas);
  const [purchaseOrders, setPurchaseOrders] = useState(propPurchaseOrders);
  const [units, setUnits] = useState([]);

  // Default FROM state (editable & persistable)
  const [defaultFrom, setDefaultFrom] = useState(() => getInitialDefaultFrom());

  // Form State (Both TO and FROM are 100% editable)
  const [formData, setFormData] = useState(() => ({
    id: '',
    stickerNo: '',
    date: new Date().toISOString().split('T')[0],
    toName: '',
    toAddressLine1: '',
    toAddressLine2: '',
    toPincode: '',
    toContactName: '',
    toContactNo: '',
    toGst: '',
    ...getInitialDefaultFrom(),
    boxCount: '1 Box',
    weight: '',
    courierName: '',
    docketNo: '',
    notes: 'Handle with care - Sensitive Mining Safety Equipment'
  }));

  // Fetch stickers from backend + local storage fallback
  const fetchStickers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dispatch-stickers');
      if (Array.isArray(res.data)) {
        setStickers(res.data);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(res.data));
        } catch (e) {}
      }
    } catch (e) {
      try {
        const local = localStorage.getItem(STORAGE_KEY);
        if (local) setStickers(JSON.parse(local));
      } catch (err) {}
    } finally {
      setLoading(false);
    }
  };

  // Load quotations, proforma invoices, purchase orders & units for quick autofill
  const fetchAutofillSources = async () => {
    try {
      const [qRes, piRes, poRes, uRes] = await Promise.allSettled([
        api.get('/quotations'),
        api.get('/proforma-invoices'),
        api.get('/purchase-orders'),
        api.get('/units')
      ]);
      if (qRes.status === 'fulfilled' && Array.isArray(qRes.value.data)) {
        setQuotations(qRes.value.data);
      }
      if (piRes.status === 'fulfilled' && Array.isArray(piRes.value.data)) {
        setProformaInvoices(piRes.value.data);
      }
      if (poRes.status === 'fulfilled' && Array.isArray(poRes.value.data)) {
        setPurchaseOrders(poRes.value.data);
      }
      if (uRes.status === 'fulfilled' && Array.isArray(uRes.value.data)) {
        setUnits(uRes.value.data);
      }
    } catch (e) {}
  };

  useEffect(() => {
    try {
      const local = localStorage.getItem(STORAGE_KEY);
      if (local) setStickers(JSON.parse(local));
    } catch (e) {}
    fetchStickers();
    fetchAutofillSources();
  }, []);

  // Filtered Stickers
  const filteredStickers = useMemo(() => {
    if (!searchTerm.trim()) return stickers;
    const q = searchTerm.toLowerCase();
    return stickers.filter((s) =>
      (s.toName && s.toName.toLowerCase().includes(q)) ||
      (s.toContactName && s.toContactName.toLowerCase().includes(q)) ||
      (s.toContactNo && s.toContactNo.toLowerCase().includes(q)) ||
      (s.toAddressLine1 && s.toAddressLine1.toLowerCase().includes(q)) ||
      (s.toAddressLine2 && s.toAddressLine2.toLowerCase().includes(q)) ||
      (s.toPincode && String(s.toPincode).includes(q)) ||
      (s.fromName && s.fromName.toLowerCase().includes(q)) ||
      (s.stickerNo && s.stickerNo.toLowerCase().includes(q)) ||
      (s.docketNo && s.docketNo.toLowerCase().includes(q))
    );
  }, [stickers, searchTerm]);

  // Helper to split a multi-line or comma-separated address into Line 1, Line 2, and Pincode
  const parseAddressParts = (rawAddress = '') => {
    const clean = String(rawAddress || '').replace(/\r/g, '').trim();
    const pinMatch = clean.match(/\b(\d{6})\b/);
    const pincode = pinMatch ? pinMatch[1] : '';

    const parts = clean
      .split(/\n|,/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (parts.length === 0) {
      return { line1: '', line2: '', pincode };
    }
    if (parts.length === 1) {
      return { line1: parts[0], line2: '', pincode };
    }
    const mid = Math.ceil(parts.length / 2);
    return {
      line1: parts.slice(0, mid).join(', '),
      line2: parts.slice(mid).join(', '),
      pincode
    };
  };

  // Open Create Modal
  const handleOpenCreateModal = () => {
    setEditingSticker(null);
    const currentFrom = getInitialDefaultFrom();
    setDefaultFrom(currentFrom);
    setFormData({
      id: `stk_${Date.now()}`,
      stickerNo: `UEE-STK-${String(stickers.length + 1).padStart(3, '0')}`,
      date: new Date().toISOString().split('T')[0],
      toName: '',
      toAddressLine1: '',
      toAddressLine2: '',
      toPincode: '',
      toContactName: '',
      toContactNo: '',
      toGst: '',
      ...currentFrom,
      boxCount: '1 Box',
      weight: '',
      courierName: '',
      docketNo: '',
      notes: 'Handle with care - Sensitive Mining Safety Equipment'
    });
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (item) => {
    setEditingSticker(item);
    setFormData({
      ...defaultFrom,
      ...item
    });
    setIsModalOpen(true);
  };

  // Apply Unit Preset (Unit 01 or Unit 02) to FROM fields
  const handleApplyFromPreset = (unitKey) => {
    const dbUnit = units.find((u) => u.key === unitKey);
    let preset = UNIT_PRESETS[unitKey] || UNIT_PRESETS.unit1;

    if (dbUnit && dbUnit.name) {
      const addr = parseAddressParts(dbUnit.address || '');
      preset = {
        fromName: dbUnit.name || preset.fromName,
        fromAddressLine1: addr.line1 || preset.fromAddressLine1,
        fromAddressLine2: addr.line2 || preset.fromAddressLine2,
        fromStatePincode: addr.pincode ? `Karnataka - ${addr.pincode}` : preset.fromStatePincode,
        fromContactName: preset.fromContactName,
        fromContactNo: dbUnit.phone || preset.fromContactNo,
        fromGstin: dbUnit.gst || preset.fromGstin
      };
    }

    setFormData((prev) => ({
      ...prev,
      ...preset
    }));
    toast.success(`Loaded ${unitKey === 'unit2' ? 'Unit 02' : 'Unit 01 (Head Office)'} sender details`);
  };

  // Save current FROM fields as default for future stickers
  const handleSaveFromAsDefault = () => {
    const newDefault = {
      fromName: formData.fromName || 'UNIVERSAL ELECTRICALS AND ELECTRONICS',
      fromAddressLine1: formData.fromAddressLine1 || '',
      fromAddressLine2: formData.fromAddressLine2 || '',
      fromStatePincode: formData.fromStatePincode || '',
      fromContactName: formData.fromContactName || '',
      fromContactNo: formData.fromContactNo || '',
      fromGstin: formData.fromGstin || ''
    };
    setDefaultFrom(newDefault);
    try {
      localStorage.setItem(CUSTOM_FROM_KEY, JSON.stringify(newDefault));
      toast.success('Current FROM (Sender) details saved as default!');
    } catch (e) {
      toast.error('Could not save default FROM details');
    }
  };

  // Quick Autofill TO details from Quotations / Proforma Invoices / Purchase Orders
  const handleAutofillTo = (e) => {
    const val = e.target.value;
    if (!val) return;

    if (val.startsWith('quote_')) {
      const qId = val.replace('quote_', '');
      const found = quotations.find((q) => String(q.id || q.quote_no || q.estNo) === qId);
      if (found) {
        const addr = parseAddressParts(found.buyer_address || found.buyerAddress || found.address || '');
        setFormData((prev) => ({
          ...prev,
          toName: found.buyer_name || found.buyerName || found.clientName || '',
          toAddressLine1: addr.line1,
          toAddressLine2: addr.line2,
          toPincode: found.buyerPincode || addr.pincode || prev.toPincode,
          toContactName: found.contact_person || found.buyerContactPerson || found.buyer_name || '',
          toContactNo: found.contact_number || found.buyerPhone || found.phone || '',
          toGst: found.buyer_gstin || found.buyerGstin || ''
        }));
        toast.success('Recipient (TO) autofilled from Quotation');
      }
    } else if (val.startsWith('pi_')) {
      const piId = val.replace('pi_', '');
      const found = proformaInvoices.find((inv) => String(inv.id || inv.pi_no || inv.invoiceNo) === piId);
      if (found) {
        const addr = parseAddressParts(found.buyer_address || found.buyerAddress || '');
        setFormData((prev) => ({
          ...prev,
          toName: found.buyer_name || found.buyerName || '',
          toAddressLine1: addr.line1,
          toAddressLine2: addr.line2,
          toPincode: found.buyerPincode || addr.pincode || prev.toPincode,
          toContactName: found.contact_person || found.buyerContactPerson || found.buyer_name || '',
          toContactNo: found.contact_number || found.buyerPhone || '',
          toGst: found.buyer_gstin || found.buyerGstin || ''
        }));
        toast.success('Recipient (TO) autofilled from Proforma Invoice');
      }
    } else if (val.startsWith('po_')) {
      const poId = val.replace('po_', '');
      const found = purchaseOrders.find((po) => String(po.id || po.po_no) === poId);
      if (found) {
        const addr = parseAddressParts(found.vendor_address || '');
        setFormData((prev) => ({
          ...prev,
          toName: found.vendor_name || '',
          toAddressLine1: addr.line1,
          toAddressLine2: addr.line2,
          toPincode: addr.pincode || prev.toPincode,
          toContactName: found.contact_person || found.vendor_name || '',
          toContactNo: found.contact_number || '',
          toGst: found.vendor_gstin || ''
        }));
        toast.success('Recipient (TO) autofilled from Purchase Order');
      }
    }
  };

  // Save Sticker (Create or Update)
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.toName || !formData.toName.trim()) {
      toast.error('Please enter Recipient (TO) Name / Company Name.');
      return;
    }
    if (!formData.fromName || !formData.fromName.trim()) {
      toast.error('Please enter Sender (FROM) Name / Company Name.');
      return;
    }

    const itemToSave = {
      ...formData,
      id: formData.id || `stk_${Date.now()}`,
      updatedAt: new Date().toISOString()
    };

    // Update local state & localStorage immediately
    setStickers((prev) => {
      const exists = prev.some((s) => String(s.id) === String(itemToSave.id));
      const updated = exists
        ? prev.map((s) => (String(s.id) === String(itemToSave.id) ? itemToSave : s))
        : [itemToSave, ...prev];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (err) {}
      return updated;
    });

    setIsModalOpen(false);
    toast.success(editingSticker ? 'Dispatch Sticker updated!' : 'Dispatch Sticker created!');

    // Sync to backend
    try {
      await api.post('/dispatch-stickers', itemToSave);
      fetchStickers();
    } catch (err) {
      // Saved in localStorage already
    }
  };

  // Delete Sticker
  const handleDelete = async (id, toName) => {
    if (!window.confirm(`Are you sure you want to delete dispatch sticker for "${toName || id}"?`)) {
      return;
    }

    setStickers((prev) => {
      const updated = prev.filter((s) => String(s.id) !== String(id));
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (err) {}
      return updated;
    });
    toast.success('Dispatch Sticker deleted');

    try {
      await api.delete(`/dispatch-stickers/${encodeURIComponent(id)}`);
      fetchStickers();
    } catch (err) {}
  };

  // Duplicate Sticker
  const handleDuplicate = (item) => {
    const dup = {
      ...item,
      id: `stk_${Date.now()}`,
      stickerNo: `UEE-STK-${String(stickers.length + 1).padStart(3, '0')}`,
      date: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString()
    };

    setStickers((prev) => {
      const updated = [dup, ...prev];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (err) {}
      return updated;
    });
    toast.success('Sticker duplicated!');

    api.post('/dispatch-stickers', dup).catch(() => {});
  };

  // Download Official Half-A4 PDF
  const handleDownloadPdf = (item) => {
    try {
      const blob = generateHalfA4StickerPdfBlob(item);
      const url = URL.createObjectURL(blob);
      const safeName = (item.toName || item.stickerNo || 'Dispatch_Sticker')
        .replace(/[\/\\:?*"<>|\s]+/g, '_')
        .replace(/^_+|_+$/g, '');
      const link = document.createElement('a');
      link.href = url;
      link.download = `UEE_Sticker_${safeName || 'Label'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      console.warn('PDF generation fallback to print:', e);
      handleDirectPrint(item);
    }
  };

  // Export to Excel / CSV
  const handleExportExcel = () => {
    if (!stickers || stickers.length === 0) {
      toast.error('No stickers available to export.');
      return;
    }

    const headers = [
      'Sticker ID',
      'Sticker No',
      'Date',
      'Recipient Name (TO)',
      'Recipient Address Line 1',
      'Recipient Address Line 2',
      'Recipient Pincode',
      'Recipient Contact Name',
      'Recipient Contact No',
      'Recipient GSTIN',
      'Sender Name (FROM)',
      'Sender Address Line 1',
      'Sender Address Line 2',
      'Sender State & PIN',
      'Sender Contact Name',
      'Sender Contact Phone',
      'Sender GSTIN',
      'Box Count',
      'Weight',
      'Courier Partner',
      'Docket / Tracking No',
      'Notes'
    ];

    const rows = stickers.map((s) => [
      `"${s.id || ''}"`,
      `"${s.stickerNo || ''}"`,
      `"${s.date || ''}"`,
      `"${(s.toName || '').replace(/"/g, '""')}"`,
      `"${(s.toAddressLine1 || '').replace(/"/g, '""')}"`,
      `"${(s.toAddressLine2 || '').replace(/"/g, '""')}"`,
      `"${s.toPincode || ''}"`,
      `"${(s.toContactName || '').replace(/"/g, '""')}"`,
      `"${s.toContactNo || ''}"`,
      `"${s.toGst || ''}"`,
      `"${(s.fromName || '').replace(/"/g, '""')}"`,
      `"${(s.fromAddressLine1 || '').replace(/"/g, '""')}"`,
      `"${(s.fromAddressLine2 || '').replace(/"/g, '""')}"`,
      `"${(s.fromStatePincode || '').replace(/"/g, '""')}"`,
      `"${(s.fromContactName || '').replace(/"/g, '""')}"`,
      `"${s.fromContactNo || ''}"`,
      `"${s.fromGstin || ''}"`,
      `"${s.boxCount || ''}"`,
      `"${s.weight || ''}"`,
      `"${(s.courierName || '').replace(/"/g, '""')}"`,
      `"${s.docketNo || ''}"`,
      `"${(s.notes || '').replace(/"/g, '""')}"`
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,\uFEFF' +
      [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `UEE_Dispatch_Stickers_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Direct Browser Print
  const handleDirectPrint = (item) => {
    setPrintStickerItem(item);
    setTimeout(() => {
      window.print();
    }, 200);
  };

  return (
    <div className="space-y-6 font-sans text-slate-900 pb-12" data-testid="dispatch-stickers-page">
      {/* Top Banner & Action Controls */}
      <div className="p-6 sm:p-7 rounded-2xl bg-white border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-lg bg-amber-500/10 text-amber-700 border border-amber-500/20 text-xs font-bold font-mono mb-2">
            <Tag className="w-3.5 h-3.5 text-amber-600" />
            <span>Universal Electricals &amp; Electronics • Official Parcel &amp; Cargo Labels</span>
          </div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            <span>Dispatch &amp; Parcel Stickers</span>
            <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-mono font-bold">
              {stickers.length} Stickers
            </span>
          </h2>
          <p className="text-xs text-slate-500 font-medium mt-1 max-w-2xl">
            Customize both <b>TO (Recipient)</b> and <b>FROM (Sender: Universal Electricals &amp; Electronics)</b> details, generate official Half-A4 shipping labels, print directly, or export in PDF &amp; Excel (.csv).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={fetchStickers}
            className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition border border-slate-200 cursor-pointer"
            title="Refresh List"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={handleExportExcel}
            className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-xs flex items-center space-x-1.5 transition active:scale-95 cursor-pointer"
            title="Export all stickers to Excel (.csv)"
          >
            <FileSpreadsheet className="w-4 h-4 text-white" />
            <span>Export Excel (.csv)</span>
          </button>

          <button
            onClick={handleOpenCreateModal}
            className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-black text-white font-bold text-xs shadow-md flex items-center space-x-2 transition active:scale-95 cursor-pointer"
            data-testid="create-sticker-btn"
          >
            <Plus className="w-4 h-4 text-amber-400" />
            <span>+ Create New Sticker</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3.5 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search by recipient (TO), sender (FROM), phone, city, state, pin, or docket no..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900 focus:outline-none"
          />
        </div>

        <div className="text-xs text-slate-500 font-medium font-mono">
          Showing {filteredStickers.length} of {stickers.length} saved labels
        </div>
      </div>

      {/* Grid of Dispatch Sticker Cards */}
      {filteredStickers.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 shadow-xs space-y-3">
          <Tag className="w-12 h-12 text-slate-300 mx-auto" />
          <h4 className="text-base font-bold text-slate-700">No Dispatch Stickers Found</h4>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            {searchTerm
              ? 'No stickers match your search query.'
              : 'Click "+ Create New Sticker" to generate your first Universal Electricals and Electronics shipping label.'}
          </p>
          <button
            onClick={handleOpenCreateModal}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold shadow hover:bg-black transition cursor-pointer"
          >
            + Create New Sticker
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 min-[1800px]:grid-cols-3 gap-6">
          {filteredStickers.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition duration-200 flex flex-col justify-between overflow-hidden"
            >
              {/* Card Header Bar */}
              <div className="p-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs">
                <div className="flex items-center space-x-2">
                  <span className="font-mono font-bold text-slate-900 px-2 py-0.5 rounded bg-white border border-slate-200">
                    {item.stickerNo || 'UEE-STK'}
                  </span>
                  <span className="text-slate-500 font-mono text-[11px]">{item.date}</span>
                </div>

                <div className="flex items-center space-x-1.5">
                  {item.boxCount && (
                    <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold font-mono">
                      📦 {item.boxCount}
                    </span>
                  )}
                  {item.weight && (
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-mono">
                      ⚖ {item.weight}
                    </span>
                  )}
                </div>
              </div>

              {/* Exact Visual Sticker Box (Premium Half-A4 Cargo Label Layout) */}
              <div className="p-4 sm:p-5 flex-1 bg-white">
                <div className="border-[2.5px] border-slate-900 rounded-xl bg-white font-sans text-slate-900 shadow-sm overflow-hidden">
                  {/* Top Brand Bar */}
                  <div className="p-3 bg-slate-50 border-b border-slate-900 flex items-center justify-between gap-2">
                    <div>
                      <div className="text-xs sm:text-sm font-black tracking-tight text-slate-900 uppercase">
                        {item.fromName || 'UNIVERSAL ELECTRICALS AND ELECTRONICS'}
                      </div>
                      <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                        An ISO 9001:2015 Certified • HEMM Safety &amp; Telemetry Systems
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] font-black text-slate-800 font-mono">
                        {item.stickerNo || 'UEE-STK'}
                      </div>
                      <div className="text-[9px] font-semibold text-slate-500">
                        {item.date || ''}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* TO Section */}
                    <div>
                      <div className="inline-block px-1.5 py-0.5 rounded bg-slate-900 text-white text-[10px] font-black tracking-wider uppercase mb-1">
                        TO:
                      </div>
                      <div className="text-base font-black leading-snug text-slate-950 uppercase">
                        {item.toName}
                      </div>
                      {item.toAddressLine1 && (
                        <div className="text-xs font-bold leading-relaxed text-slate-800 mt-0.5">
                          {item.toAddressLine1}
                        </div>
                      )}
                      {item.toAddressLine2 && (
                        <div className="text-xs font-bold leading-relaxed text-slate-800">
                          {item.toAddressLine2}
                        </div>
                      )}
                      {item.toPincode && (
                        <div className="text-xs font-black tracking-wider text-slate-950 mt-1 font-mono">
                          PIN CODE : {item.toPincode}
                        </div>
                      )}
                      <div className="mt-2 text-xs font-bold leading-snug text-slate-900">
                        Contact Name : <span className="font-bold">{item.toContactName || item.toName}</span>
                      </div>
                      <div className="text-xs font-bold leading-snug font-mono text-slate-900">
                        Contact No : <span className="font-bold">{item.toContactNo}</span>
                      </div>
                      {item.toGst && (
                        <div className="text-xs font-black leading-snug font-mono text-slate-950 mt-0.5">
                          GSTIN : {item.toGst}
                        </div>
                      )}
                    </div>

                    {/* FROM Section: "FROM," left-aligned, Company Name & Address centered */}
                    <div className="pt-4 border-t-2 border-dashed border-slate-300">
                      <div className="text-sm font-black tracking-wide text-slate-950 mb-1">
                        FROM,
                      </div>
                      <div className="text-center my-1.5">
                        <div className="text-base sm:text-lg font-black tracking-tight text-slate-950 uppercase">
                          {item.fromName || 'UNIVERSAL ELECTRICALS AND ELECTRONICS'}
                        </div>
                      </div>
                      <div className="space-y-0.5 mt-2 text-center">
                        {item.fromAddressLine1 && (
                          <div className="text-xs font-bold leading-normal text-slate-800">
                            {item.fromAddressLine1}
                          </div>
                        )}
                        {item.fromAddressLine2 && (
                          <div className="text-xs font-bold leading-normal text-slate-800">
                            {item.fromAddressLine2}
                          </div>
                        )}
                        {item.fromStatePincode && (
                          <div className="text-xs font-black tracking-wider text-slate-950">
                            {item.fromStatePincode}
                          </div>
                        )}
                        {item.fromContactName && (
                          <div className="pt-1 text-xs font-bold leading-normal text-slate-900">
                            Contact Name : {item.fromContactName}
                          </div>
                        )}
                        {item.fromContactNo && (
                          <div className="text-xs font-bold font-mono text-slate-900">
                            Contact No : {item.fromContactNo}
                          </div>
                        )}
                        {item.fromGstin && (
                          <div className="text-xs font-black font-mono text-slate-950">
                            GSTIN : {item.fromGstin}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Strip */}
                  <div className="px-3 py-1 bg-slate-100 border-t border-slate-200 text-center text-[9px] font-bold text-slate-600">
                    • {item.notes ? item.notes.toUpperCase() : 'HANDLE WITH CARE — SENSITIVE EQUIPMENT'} •
                  </div>
                </div>
              </div>

              {/* Action Buttons Bar */}
              <div className="p-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-2 flex-wrap text-xs">
                <div className="flex items-center space-x-2">
                  {/* Print Button */}
                  <button
                    onClick={() => handleDirectPrint(item)}
                    className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-black text-white font-bold transition flex items-center space-x-1 cursor-pointer shadow-xs active:scale-95"
                    title="Print Sticker directly"
                  >
                    <Printer className="w-3.5 h-3.5 text-amber-400" />
                    <span>Print</span>
                  </button>

                  {/* PDF Download Button */}
                  <button
                    onClick={() => handleDownloadPdf(item)}
                    className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold transition flex items-center space-x-1 cursor-pointer shadow-xs active:scale-95"
                    title="Download Official Half-A4 PDF"
                  >
                    <FileDown className="w-3.5 h-3.5 text-white" />
                    <span>PDF</span>
                  </button>
                </div>

                <div className="flex items-center space-x-1.5">
                  {/* Duplicate Button */}
                  <button
                    onClick={() => handleDuplicate(item)}
                    className="p-1.5 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition cursor-pointer"
                    title="Duplicate Sticker"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>

                  {/* Edit Button */}
                  <button
                    onClick={() => handleOpenEditModal(item)}
                    className="px-2.5 py-1.5 rounded-lg text-slate-700 hover:text-slate-950 bg-white hover:bg-slate-200 border border-slate-200 font-bold transition flex items-center space-x-1 cursor-pointer"
                    title="Edit Both TO & FROM Sticker Details"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Edit</span>
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDelete(item.id, item.toName)}
                    className="p-1.5 rounded-lg text-rose-600 hover:text-rose-800 hover:bg-rose-50 transition cursor-pointer"
                    title="Delete Sticker"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CREATE / EDIT STICKER MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn overflow-y-auto">
          <div className="relative w-full max-w-6xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden my-6">
            {/* Modal Header */}
            <div className="p-5 sm:p-6 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  <Tag className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black tracking-tight text-white">
                    {editingSticker ? 'Edit Dispatch Sticker (TO & FROM Editable)' : 'Create New Dispatch Sticker (TO & FROM Editable)'}
                  </h3>
                  <p className="text-xs text-slate-400 font-mono">
                    Universal Electricals and Electronics • Official Half-A4 Cargo Box / Parcel Label
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body: Form & Live Preview */}
            <form onSubmit={handleSubmit} className="p-6 max-h-[82vh] overflow-y-auto font-sans text-xs space-y-6">
              {/* Quick Autofill Selector Bar */}
              <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center space-x-2 text-amber-900 font-bold">
                  <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>Quick Autofill Recipient (TO) from Existing Records:</span>
                </div>

                <select
                  onChange={handleAutofillTo}
                  defaultValue=""
                  className="px-3 py-2 rounded-xl bg-white border border-amber-300 text-slate-900 font-semibold text-xs focus:ring-2 focus:ring-amber-500 focus:outline-none max-w-md w-full"
                >
                  <option value="" disabled>
                    -- Select Client / Vendor from Quotations, Proforma or PO --
                  </option>
                  {quotations.map((q) => (
                    <option key={`q_${q.id || q.quote_no}`} value={`quote_${q.id || q.quote_no}`}>
                      📄 {q.quote_no || q.estNo || 'QTN'} — {q.buyer_name || q.buyerName || 'Quotation'}
                    </option>
                  ))}
                  {proformaInvoices.map((inv) => (
                    <option key={`pi_${inv.id || inv.pi_no}`} value={`pi_${inv.id || inv.pi_no}`}>
                      🧾 {inv.pi_no || inv.invoiceNo || 'PI'} — {inv.buyer_name || inv.buyerName || 'Proforma'}
                    </option>
                  ))}
                  {purchaseOrders.map((po) => (
                    <option key={`po_${po.id || po.po_no}`} value={`po_${po.id || po.po_no}`}>
                      🛒 {po.po_no || 'PO'} — {po.vendor_name || 'Vendor'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* LEFT COLUMN (7 cols): Editable TO, Editable FROM, and Parcel Metadata */}
                <div className="lg:col-span-7 space-y-5">
                  {/* Label No & Date */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">
                        Sticker / Ref No *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.stickerNo}
                        onChange={(e) => setFormData({ ...formData, stickerNo: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-mono font-bold focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">
                        Dispatch Date
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-mono focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* 1. TO (RECIPIENT / CONSIGNEE DETAILS) — FULLY EDITABLE */}
                  <div className="p-4 rounded-2xl bg-blue-50/40 border-2 border-blue-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <span>1. TO (Consignee / Recipient Details) — Editable</span>
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                        Receiver
                      </span>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-700 block mb-1">
                        Recipient / Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Mahanadi Coalfields Ltd / Sandip Behera"
                        value={formData.toName}
                        onChange={(e) => setFormData({ ...formData, toName: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-bold focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-700 block mb-1">
                        Address Line 1 (Street / Area / Building)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Vyasanagar, Back side of"
                        value={formData.toAddressLine1}
                        onChange={(e) => setFormData({ ...formData, toAddressLine1: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-700 block mb-1">
                        Address Line 2 (Landmark, City, State)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Vyasanagar Autonomous College, Jajpur Road, Odisha"
                        value={formData.toAddressLine2}
                        onChange={(e) => setFormData({ ...formData, toAddressLine2: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div>
                        <label className="text-[10px] font-bold text-slate-700 block mb-1">
                          PIN Code
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. 755019"
                          value={formData.toPincode}
                          onChange={(e) => setFormData({ ...formData, toPincode: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-mono font-bold focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-700 block mb-1">
                          Contact Person Name
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Sandip Behera"
                          value={formData.toContactName}
                          onChange={(e) => setFormData({ ...formData, toContactName: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-700 block mb-1">
                          Contact Phone / Mobile *
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. +91 8093582128"
                          value={formData.toContactNo}
                          onChange={(e) => setFormData({ ...formData, toContactNo: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-mono font-bold focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-700 block mb-1">
                        Recipient GSTIN (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. 21AAACM..."
                        value={formData.toGst || ''}
                        onChange={(e) => setFormData({ ...formData, toGst: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-mono focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* 2. FROM (SENDER / CONSIGNOR DETAILS) — FULLY EDITABLE & OPEN */}
                  <div className="p-4 rounded-2xl bg-amber-50/50 border-2 border-amber-300 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-amber-600" />
                        <span>2. FROM (Sender Details) — Editable</span>
                      </span>

                      {/* Unit Preset & Save Default Buttons */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleApplyFromPreset('unit1')}
                          className="px-2.5 py-1 rounded-lg bg-white hover:bg-amber-100 text-slate-800 border border-amber-300 text-[10px] font-bold transition cursor-pointer flex items-center gap-1"
                          title="Fill Unit 01 Head Office address"
                        >
                          <RotateCcw className="w-3 h-3 text-amber-600" />
                          <span>Unit 01 (Abbigere)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleApplyFromPreset('unit2')}
                          className="px-2.5 py-1 rounded-lg bg-white hover:bg-amber-100 text-slate-800 border border-amber-300 text-[10px] font-bold transition cursor-pointer flex items-center gap-1"
                          title="Fill Unit 02 Kempapura address"
                        >
                          <RotateCcw className="w-3 h-3 text-amber-600" />
                          <span>Unit 02 (Kempapura)</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveFromAsDefault}
                          className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-black text-white text-[10px] font-bold transition cursor-pointer flex items-center gap-1"
                          title="Save current FROM details as default for future stickers"
                        >
                          <BookmarkCheck className="w-3 h-3 text-amber-400" />
                          <span>Save as Default</span>
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-700 block mb-1">
                        Sender Company Name (FROM) *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="UNIVERSAL ELECTRICALS AND ELECTRONICS"
                        value={formData.fromName}
                        onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-bold focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-700 block mb-1">
                        Sender Address Line 1
                      </label>
                      <input
                        type="text"
                        placeholder="15 & 16, 1st Floor, Girija Arcade, Lakshmipura Main Road,"
                        value={formData.fromAddressLine1}
                        onChange={(e) => setFormData({ ...formData, fromAddressLine1: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-700 block mb-1">
                        Sender Address Line 2
                      </label>
                      <input
                        type="text"
                        placeholder="Opp PMK, Abbigere, Bangalore,"
                        value={formData.fromAddressLine2}
                        onChange={(e) => setFormData({ ...formData, fromAddressLine2: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="text-[10px] font-bold text-slate-700 block mb-1">
                          Sender State &amp; PIN Code
                        </label>
                        <input
                          type="text"
                          placeholder="Karnataka - 560090"
                          value={formData.fromStatePincode}
                          onChange={(e) =>
                            setFormData({ ...formData, fromStatePincode: e.target.value })
                          }
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-bold focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-700 block mb-1">
                          Sender Contact Person / Name
                        </label>
                        <input
                          type="text"
                          placeholder="UNIVERSAL ELECTRICALS & ELECTRONICS"
                          value={formData.fromContactName}
                          onChange={(e) =>
                            setFormData({ ...formData, fromContactName: e.target.value })
                          }
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="text-[10px] font-bold text-slate-700 block mb-1">
                          Sender Contact No
                        </label>
                        <input
                          type="text"
                          placeholder="+91 84949 00615"
                          value={formData.fromContactNo}
                          onChange={(e) => setFormData({ ...formData, fromContactNo: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-mono font-bold focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-700 block mb-1">
                          Sender GSTIN
                        </label>
                        <input
                          type="text"
                          placeholder="29AHKPH7026A1ZK"
                          value={formData.fromGstin}
                          onChange={(e) => setFormData({ ...formData, fromGstin: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-mono font-bold focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 3. Parcel & Courier Metadata */}
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-amber-600" />
                      <span>3. Parcel &amp; Courier Metadata</span>
                    </span>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-slate-600 block mb-1">
                          Box Count
                        </label>
                        <input
                          type="text"
                          placeholder="1 Box"
                          value={formData.boxCount}
                          onChange={(e) => setFormData({ ...formData, boxCount: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-mono focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-600 block mb-1">
                          Gross Weight
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. 5 KG"
                          value={formData.weight}
                          onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-mono focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-600 block mb-1">
                          Courier Partner
                        </label>
                        <input
                          type="text"
                          placeholder="DTDC / Delhivery / VRL"
                          value={formData.courierName}
                          onChange={(e) => setFormData({ ...formData, courierName: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-600 block mb-1">
                          Docket / AWB No
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. D38914028"
                          value={formData.docketNo}
                          onChange={(e) => setFormData({ ...formData, docketNo: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 font-mono focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-600 block mb-1">
                        Handling Instruction / Bottom Caution Note
                      </label>
                      <input
                        type="text"
                        placeholder="Handle with care - Sensitive Mining Safety Equipment"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN (5 cols): Live Exact Preview */}
                <div className="lg:col-span-5 space-y-3 flex flex-col justify-between">
                  <div className="sticky top-2">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center justify-between mb-2">
                      <span>Live Sticker Print Preview</span>
                      <span className="text-[10px] text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        Half-A4 1:1 Layout
                      </span>
                    </span>

                    <div className="border-[2.5px] border-slate-900 rounded-xl bg-white font-sans text-slate-900 shadow-sm overflow-hidden">
                      {/* Top Brand Bar */}
                      <div className="p-3 bg-slate-50 border-b border-slate-900 flex items-center justify-between gap-2">
                        <div>
                          <div className="text-xs sm:text-sm font-black tracking-tight text-slate-900 uppercase">
                            {formData.fromName || 'UNIVERSAL ELECTRICALS AND ELECTRONICS'}
                          </div>
                          <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                            An ISO 9001:2015 Certified • HEMM Safety &amp; Telemetry Systems
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] font-black text-slate-800 font-mono">
                            {formData.stickerNo || 'UEE-STK-001'}
                          </div>
                          <div className="text-[9px] font-semibold text-slate-500">
                            {formData.date || ''}
                          </div>
                        </div>
                      </div>

                      <div className="p-4 space-y-4">
                        {/* TO Section */}
                        <div>
                          <div className="inline-block px-1.5 py-0.5 rounded bg-slate-900 text-white text-[10px] font-black tracking-wider uppercase mb-1">
                            TO:
                          </div>
                          <div className="text-base font-black leading-snug text-slate-950 uppercase">
                            {formData.toName || 'RECIPIENT / COMPANY NAME'}
                          </div>
                          <div className="text-xs font-bold leading-relaxed text-slate-800 mt-0.5">
                            {formData.toAddressLine1 || 'Address Line 1'}
                          </div>
                          <div className="text-xs font-bold leading-relaxed text-slate-800">
                            {formData.toAddressLine2 || 'Address Line 2, City, State'}
                          </div>
                          <div className="text-xs font-black tracking-wider text-slate-950 mt-1 font-mono">
                            PIN CODE : {formData.toPincode || '000000'}
                          </div>
                          <div className="mt-2 text-xs font-bold leading-snug text-slate-900">
                            Contact Name :{' '}
                            <span className="font-bold">
                              {formData.toContactName || formData.toName || '—'}
                            </span>
                          </div>
                          <div className="text-xs font-bold leading-snug font-mono text-slate-900">
                            Contact No :{' '}
                            <span className="font-bold">{formData.toContactNo || '—'}</span>
                          </div>
                          {formData.toGst && (
                            <div className="text-xs font-black leading-snug font-mono text-slate-950 mt-0.5">
                              GSTIN : {formData.toGst}
                            </div>
                          )}
                        </div>

                        {/* FROM Section */}
                        <div className="pt-4 border-t-2 border-dashed border-slate-300">
                          <div className="text-sm font-black tracking-wide text-slate-950 mb-1">
                            FROM,
                          </div>
                          <div className="text-center my-1.5">
                            <div className="text-base sm:text-lg font-black tracking-tight text-slate-950 uppercase">
                              {formData.fromName || 'UNIVERSAL ELECTRICALS AND ELECTRONICS'}
                            </div>
                          </div>
                          <div className="space-y-0.5 mt-2 text-center">
                            <div className="text-xs font-bold leading-normal text-slate-800">
                              {formData.fromAddressLine1}
                            </div>
                            <div className="text-xs font-bold leading-normal text-slate-800">
                              {formData.fromAddressLine2}
                            </div>
                            <div className="text-xs font-black tracking-wider text-slate-950">
                              {formData.fromStatePincode}
                            </div>
                            {formData.fromContactName && (
                              <div className="pt-1 text-xs font-bold leading-normal text-slate-900">
                                Contact Name : {formData.fromContactName}
                              </div>
                            )}
                            {formData.fromContactNo && (
                              <div className="text-xs font-bold font-mono text-slate-900">
                                Contact No : {formData.fromContactNo}
                              </div>
                            )}
                            {formData.fromGstin && (
                              <div className="text-xs font-black font-mono text-slate-950">
                                GSTIN : {formData.fromGstin}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Bottom Strip */}
                      <div className="px-3 py-1 bg-slate-100 border-t border-slate-200 text-center text-[9px] font-bold text-slate-600">
                        •{' '}
                        {formData.notes
                          ? formData.notes.toUpperCase()
                          : 'HANDLE WITH CARE — SENSITIVE EQUIPMENT'}{' '}
                        •
                      </div>
                    </div>

                    <p className="text-[11px] text-slate-500 italic text-center mt-3">
                      ✂ Both <b>TO</b> and <b>FROM</b> sections are live-synced above and print in crisp black vector format on Half-A4 sheets.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Action Footer */}
              <div className="pt-4 border-t border-slate-200 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-black text-white font-bold text-xs shadow-md transition flex items-center space-x-2 cursor-pointer active:scale-95"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>{editingSticker ? 'Update Sticker' : 'Save & Create Sticker'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* HIDDEN PRINT CONTAINER (Triggered on Print - Calibrated for Half-A4 / 148.5mm height) */}
      {printStickerItem && (
        <div id="sticker-print-area" className="hidden print:block fixed inset-0 bg-white z-50">
          <style
            dangerouslySetInnerHTML={{
              __html: `
            @media print {
              @page {
                size: A4 portrait;
                margin: 0;
              }
              body {
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              body * { visibility: hidden; }
              #sticker-print-area, #sticker-print-area * { visibility: visible; }
              #sticker-print-area {
                position: absolute;
                left: 0;
                top: 0;
                width: 210mm;
                height: 297mm;
                display: block !important;
                box-sizing: border-box;
                padding: 10mm 12mm;
                background: #fff !important;
              }
              .print-half-a4-sticker {
                width: 186mm;
                max-height: 132mm;
                border: 2.5px solid #000 !important;
                border-radius: 6px;
                box-sizing: border-box;
                margin: 0 auto;
                font-family: Arial, Helvetica, sans-serif !important;
                color: #000 !important;
                overflow: hidden;
                background: #fff !important;
              }
              .print-cut-line {
                width: 186mm;
                margin: 6mm auto 0 auto;
                text-align: center;
                border-top: 1.5px dashed #64748b;
                padding-top: 3mm;
                font-size: 10px;
                font-weight: 700;
                color: #475569;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
            }
          `
            }}
          />

          <div className="print-half-a4-sticker">
            {/* Top Brand & Metadata Bar */}
            <div
              style={{
                padding: '6px 14px',
                background: '#f8fafc',
                borderBottom: '1.5px solid #000',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: '900',
                    letterSpacing: '0.4px',
                    color: '#0f172a',
                    lineHeight: '1.1',
                    textTransform: 'uppercase'
                  }}
                >
                  {printStickerItem.fromName || 'UNIVERSAL ELECTRICALS AND ELECTRONICS'}
                </div>
                <div
                  style={{
                    fontSize: '9px',
                    fontWeight: '800',
                    color: '#475569',
                    letterSpacing: '0.3px',
                    marginTop: '1px'
                  }}
                >
                  AN ISO 9001:2015 CERTIFIED • HEMM VEHICLES SAFETY EQUIPMENTS • DISPATCH STICKER
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '11px', fontWeight: '900', color: '#0f172a' }}>
                  {printStickerItem.stickerNo || 'UEE-STK'}{' '}
                  {printStickerItem.date ? ` |  ${printStickerItem.date}` : ''}
                </div>
                <div style={{ fontSize: '9.5px', fontWeight: '800', color: '#475569', marginTop: '1px' }}>
                  {printStickerItem.boxCount ? `PKG: ${printStickerItem.boxCount}` : '1 Box'}
                  {printStickerItem.weight
                    ? `  •  WT: ${
                        printStickerItem.weight.toString().toUpperCase().includes('KG')
                          ? printStickerItem.weight
                          : printStickerItem.weight + ' KG'
                      }`
                    : ''}
                  {printStickerItem.docketNo ? `  •  AWB: ${printStickerItem.docketNo}` : ''}
                </div>
              </div>
            </div>

            {/* TO (Consignee) Section */}
            <div style={{ padding: '10px 14px', borderBottom: '1.5px dashed #000' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: '900',
                    lineHeight: '1',
                    minWidth: '42px',
                    color: '#000'
                  }}
                >
                  TO
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '19px',
                      fontWeight: '900',
                      lineHeight: '1.2',
                      color: '#000',
                      textTransform: 'uppercase'
                    }}
                  >
                    {printStickerItem.toName}
                  </div>
                  {printStickerItem.toAddressLine1 && (
                    <div
                      style={{
                        fontSize: '13.5px',
                        fontWeight: '700',
                        lineHeight: '1.3',
                        marginTop: '2px',
                        color: '#000'
                      }}
                    >
                      {printStickerItem.toAddressLine1}
                    </div>
                  )}
                  {printStickerItem.toAddressLine2 && (
                    <div
                      style={{
                        fontSize: '13.5px',
                        fontWeight: '700',
                        lineHeight: '1.3',
                        marginTop: '1px',
                        color: '#000'
                      }}
                    >
                      {printStickerItem.toAddressLine2}
                    </div>
                  )}
                  {printStickerItem.toPincode && (
                    <div
                      style={{
                        fontSize: '14.5px',
                        fontWeight: '900',
                        marginTop: '2px',
                        color: '#000',
                        letterSpacing: '0.5px'
                      }}
                    >
                      PIN CODE : {printStickerItem.toPincode}
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '16px',
                      marginTop: '6px',
                      fontSize: '12.5px',
                      fontWeight: '800',
                      color: '#000'
                    }}
                  >
                    <div>
                      Contact Name : {printStickerItem.toContactName || printStickerItem.toName}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontWeight: '900' }}>
                      Contact No : {printStickerItem.toContactNo}
                    </div>
                    {printStickerItem.toGst && <div>GSTIN : {printStickerItem.toGst}</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* FROM (Sender Universal Electricals and Electronics) Section */}
            <div style={{ padding: '10px 14px', background: '#ffffff', textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '17px',
                  fontWeight: '900',
                  color: '#000',
                  textAlign: 'left',
                  marginBottom: '2px'
                }}
              >
                FROM,
              </div>
              <div style={{ margin: '4px 0' }}>
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: '900',
                    color: '#000',
                    textTransform: 'uppercase'
                  }}
                >
                  {printStickerItem.fromName || 'UNIVERSAL ELECTRICALS AND ELECTRONICS'}
                </div>
              </div>
              {printStickerItem.fromAddressLine1 && (
                <div style={{ fontSize: '13px', fontWeight: '700', lineHeight: '1.35', color: '#000' }}>
                  {printStickerItem.fromAddressLine1}
                </div>
              )}
              {printStickerItem.fromAddressLine2 && (
                <div style={{ fontSize: '13px', fontWeight: '700', lineHeight: '1.35', color: '#000' }}>
                  {printStickerItem.fromAddressLine2}
                </div>
              )}
              {printStickerItem.fromStatePincode && (
                <div style={{ fontSize: '13.5px', fontWeight: '900', lineHeight: '1.35', color: '#000' }}>
                  {printStickerItem.fromStatePincode}
                </div>
              )}

              <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: '800', color: '#000' }}>
                {printStickerItem.fromContactName && (
                  <div>Contact Name : {printStickerItem.fromContactName}</div>
                )}
                {printStickerItem.fromContactNo && (
                  <div style={{ fontFamily: 'monospace' }}>
                    Contact No : {printStickerItem.fromContactNo}
                  </div>
                )}
                {printStickerItem.fromGstin && (
                  <div style={{ fontFamily: 'monospace' }}>
                    GSTIN : {printStickerItem.fromGstin}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Caution Banner */}
            <div
              style={{
                padding: '3px 10px',
                background: '#f1f5f9',
                borderTop: '1px solid #cbd5e1',
                textAlign: 'center',
                fontSize: '8.5px',
                fontWeight: '800',
                color: '#334155',
                letterSpacing: '0.5px'
              }}
            >
              •{' '}
              {printStickerItem.notes
                ? printStickerItem.notes.toUpperCase()
                : 'HANDLE WITH CARE — SENSITIVE MINING EQUIPMENT'}{' '}
              • DO NOT CRUSH •
            </div>
          </div>

          {/* Half-A4 Cut Guide */}
          <div className="print-cut-line">
            ✂ — — — — — — — HALF-A4 CUT LINE (FOLD OR CUT HERE &amp; PASTE ON PARCEL BOX) — — — — — — — ✂
          </div>
        </div>
      )}
    </div>
  );
}

